import { Injectable } from '@angular/core';
import { ModalService } from 'src/app/components/modals/modal.service';

export type OverwriteDecision = 'replace' | 'keepBoth';

@Injectable({
  providedIn: 'root',
})
export class UploadOverwriteWarningService {
  private pendingChecks = 0;
  private conflictFileNames: string[] = [];
  private decisionPromise?: Promise<OverwriteDecision>;
  private resolveDecision?: (decision: OverwriteDecision) => void;
  private rejectDecision?: (reason: Error) => void;
  private dialogOpen = false;
  private decisionSettled = false;

  constructor(private modalService: ModalService) {}

  reset() {
    if (
      this.pendingChecks > 0 ||
      (this.decisionPromise && !this.decisionSettled)
    ) {
      return;
    }

    this.pendingChecks = 0;
    this.conflictFileNames = [];
    this.decisionPromise = undefined;
    this.resolveDecision = undefined;
    this.rejectDecision = undefined;
    this.dialogOpen = false;
    this.decisionSettled = false;
  }

  registerPendingCheck() {
    this.pendingChecks++;
  }

  completePendingCheck() {
    if (this.pendingChecks > 0) {
      this.pendingChecks--;
    }
    this.showDialogWhenReady();
  }

  requestDecision(fileName: string): Promise<OverwriteDecision> {
    this.ensureDecisionPromise();

    // Conflicts surfacing after the dialog opened (addToQueue enqueues in
    // waves) adopt the batch decision without changing the displayed count.
    if (!this.dialogOpen) {
      this.conflictFileNames.push(fileName);
    }

    this.showDialogWhenReady();

    return this.decisionPromise;
  }

  requestBatchDecision(fileNames: string[]): Promise<OverwriteDecision> {
    if (this.decisionSettled) {
      this.reset();
    }

    this.ensureDecisionPromise();

    if (!this.dialogOpen) {
      this.conflictFileNames.push(...fileNames);
    }

    this.openDialog();

    return this.decisionPromise;
  }

  private ensureDecisionPromise() {
    if (!this.decisionPromise) {
      this.decisionPromise = new Promise((resolve, reject) => {
        this.resolveDecision = resolve;
        this.rejectDecision = reject;
      });
    }
  }

  private showDialogWhenReady() {
    if (this.pendingChecks > 0) {
      return;
    }
    this.openDialog();
  }

  private async openDialog() {
    if (this.dialogOpen || !this.conflictFileNames.length) {
      return;
    }
    this.dialogOpen = true;

    const count = this.conflictFileNames.length;
    const title =
      count === 1 ? 'Duplicate file detected' : 'Duplicate files detected';
    const message =
      count === 1
        ? `A file named “${this.escapeHtml(this.conflictFileNames[0])}” already exists in this folder. ` +
          'Choose whether to keep both files or replace the existing file.'
        : `${count} files already exist in this folder. ` +
          'Choose whether to keep both files or replace the existing files.';

    try {
      const choice: true | 'secondary' = await this.modalService.confirm(
        title,
        message,
        'Keep Both Files',
        'Cancel',
        undefined,
        {
          panelClass: ['duplicate-file-dialog'],
          role: 'alertdialog',
          autoFocus: '#confirm-primary',
        },
        'Replace Existing File',
      );
      this.decisionSettled = true;
      this.resolveDecision?.(choice === true ? 'keepBoth' : 'replace');
    } catch {
      this.decisionSettled = true;
      this.rejectDecision?.(new Error('Upload cancelled'));
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
